/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { PriceComponent } from '@msdyn365-commerce/components';
import { DeliveryOption } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';
import DescriptionComponent from './dlvry-checkout-v2-delivery-options.description';
import RadioButtonComponent from './dlvry-checkout-v2-delivery-options.radio-button';

export interface ICheckoutCartLineDeliveryOptionsRadioButtonProps {
    moduleId: string;
    ariaSetSize: number;
    priceFree: string;
    ariaPosInSet: number;
    selectedItemId: string;
    deliveryOptions: DeliveryOption[];
    typeName: string;
    context: ICoreContext<IGeneric<IAny>>;
    ariaLabel?: string;
    value?: string;
    telemetryContent?: ITelemetryContent;
    onChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

/**
 * Represents a radio button component for checkout cart line delivery options.
 * @param {Object} props - The component props.
 * @param {boolean} props.isChecked - Indicates whether the radio button is checked.
 * @param {string} props.value - The value of the radio button.
 * @param {number} props.ariaSetSize - The total number of radio buttons in the set.
 * @param {number} props.ariaPosInSet - The position of the radio button in the set.
 * @param {string} props.ariaLabel - The ARIA label for the radio button.
 * @param {string} props.telemetryContent - The telemetry content for the radio button.
 * @param {Function} props.onChange - The event handler for when the radio button is changed.
 * @returns {JSX.Element} The rendered radio button component.
 */
const CheckoutCartLineDeliveryOptionsRadio: React.FC<ICheckoutCartLineDeliveryOptionsRadioButtonProps> = ({
    moduleId,
    ariaSetSize,
    ariaPosInSet,
    ariaLabel,
    telemetryContent,
    deliveryOptions,
    priceFree,
    selectedItemId,
    typeName,
    context,
    onChange
}) => {
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.DeliveryOptions);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);
    console.log(attributes);
    const channelConfig = context.actionContext.requestContext.channel;
    const count = deliveryOptions.length;
    if (count === 0) {
        return <div>{'hello'}</div>;
    }
    const response = deliveryOptions.map((deliveryOption: DeliveryOption, index: number) => (
        <div key={index}>
            <RadioButtonComponent
                {...{
                    isChecked: deliveryOption.Code === selectedItemId,
                    value: deliveryOption.Code,
                    ariaSetSize: count,
                    ariaPosInSet: index + 1,
                    ariaLabel: `${deliveryOption.Description!}`,
                    onChange: onChange,
                    telemetryContent: telemetryContent
                }}
            />
            <DescriptionComponent
                {...{
                    description: deliveryOption.Description
                }}
            />
            <PriceComponent
                id={moduleId}
                typeName={typeName}
                context={context}
                className='ms-checkout-delivery-options__price'
                data={{ price: { CustomerContextualPrice: get(deliveryOption, 'ShippingChargeAmount', 0) } }}
                freePriceText={priceFree}
                currencyCode={channelConfig?.Currency}
            />
        </div>
    ));

    return <div>{response}</div>;
};

export default CheckoutCartLineDeliveryOptionsRadio;
